import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import ChatMessage from './ChatMessage';
import { COUNTRIES, APPLIANCE_METADATA } from '../../utils/constants';
import { SOLAR_DATASET } from '../../utils/solarDataset';

const APPLIANCE_CATS = {
  "Essentials": ["Lights", "Refrigerator / Freezer", "WiFi / Internet", "Phone charging"],
  "Living": ["TV", "Fan", "Laptop / Computer"],
  "Cooling / Heating": ["Air Conditioner", "Heater / Heat Pump"],
  "Kitchen": ["Microwave", "Electric kettle", "Stove / Oven"],
  "Laundry": ["Washing machine", "Iron"],
  "Water & Utilities": ["Water pump", "Water heater"],
  "Advanced": ["EV charger", "Dishwasher", "Air fryer", "Security / CCTV"]
};

const HOUR_OPTIONS = Array.from({ length: 25 }, (_, i) => i);

export default function ChatWizard({ onComplete, onClose }) {
  const [data, setData] = useState({
    country: '',
    city: '',
    peak_sun_hours: 0,
    usage_type: '',
    goal: '',
    appliances: [],
    appliance_count: {},
    appliance_hours: {},
    reliability: '',
    electricity_bill: '',
    generator: '',
    fuel_cost: '',
    solar_ready: '',
    name: '',
    email: '',
    phone: ''
  });

  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [phase, setPhase] = useState('country');
  const scrollRef = useRef(null);

  const phases = {
    country: {
      ai: ["Welcome to MaDaNRG! I'm your energy advisor.", "Which country will this energy solution be used in?"],
      type: 'search-select',
      options: COUNTRIES.map(c => c.name),
      key: 'country',
      next: 'location'
    },
    location: {
      ai: ["Where exactly will the solar system be installed?", "(We use this to look up your local peak sun hours)"],
      type: 'city-select',
      key: 'city',
      next: 'usage_type'
    },
    usage_type: {
      ai: [(val, d) => `We use conservative (worst-month installer-grade) peak sun hours of ${d.peak_sun_hours} hrs/day for system sizing in ${d.city}.`, "Is this for your home or business?"],
      type: 'options',
      options: ['Home', 'Business'],
      key: 'usage_type',
      next: 'goal'
    },
    goal: {
      ai: ["What is your main energy goal?"],
      type: 'options',
      options: ['Save money', 'Reduce outages', 'Energy independence', 'Sell power'],
      key: 'goal',
      next: 'appliances'
    },
    appliances: {
      ai: ["Which appliances do you want to power?"],
      type: 'multi-categorized',
      key: 'appliances',
      next: 'appliance-counts'
    },
    'appliance-counts': {
      ai: ["How many of each appliance do you use?"],
      type: 'counts',
      key: 'appliance_count',
      next: 'appliance-hours'
    },
    'appliance-hours': {
      ai: ["How many hours per day will each appliance be used?"],
      type: 'hours-dropdown',
      key: 'appliance_hours',
      next: 'reliability'
    },
    reliability: {
      ai: ["How would you describe your grid reliability?"],
      type: 'options',
      options: ['Very reliable', 'Occasional outages', 'Frequent outages', 'No stable grid'],
      key: 'reliability',
      next: 'electricity_bill'
    },
    electricity_bill: {
      ai: ["What is your average monthly electricity bill?"],
      type: 'options',
      options: ['Very low', 'Moderate', 'High', 'Very high'],
      key: 'electricity_bill',
      next: 'generator'
    },
    generator: {
      ai: ["Do you currently use a generator?"],
      type: 'options',
      options: ['Yes', 'No'],
      key: 'generator',
      next: (val) => val === 'Yes' ? 'fuel' : 'solar_ready'
    },
    fuel: {
      ai: ["Monthly generator fuel spend?"],
      type: 'options',
      options: ['Low', 'Moderate', 'High', 'Very high'],
      key: 'fuel_cost',
      next: 'solar_ready'
    },
    solar_ready: {
      ai: ["Space for solar panels?"],
      type: 'options',
      options: ['Yes', 'No', 'Not sure'],
      key: 'solar_ready',
      next: 'contact'
    },
    contact: {
      ai: ["Enter your details to receive your customized energy plan."],
      type: 'contact-form',
      key: 'contact',
      next: 'calculating'
    }
  };

  useEffect(() => {
    if (phase === 'calculating') {
      setIsTyping(true);
      setTimeout(() => onComplete(data), 2000);
      return;
    }
    const currentPhase = phases[phase];
    if (!currentPhase) return;

    setIsTyping(true);
    let delay = 0;
    currentPhase.ai.forEach((textOrFn, index) => {
      const text = typeof textOrFn === 'function' ? textOrFn(null, data) : textOrFn;
      delay += 800 + Math.random() * 400;
      setTimeout(() => {
        setMessages(prev => [...prev, { id: Date.now() + index, sender: 'ai', text }]);
        if (index === currentPhase.ai.length - 1) setIsTyping(false);
      }, delay);
    });
  }, [phase]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isTyping]);

  const handleInputSubmit = (val, customLabel, extraData = {}) => {
    const currentPhase = phases[phase];
    setMessages(prev => [...prev, { id: Date.now(), sender: 'user', text: customLabel || (Array.isArray(val) ? val.join(', ') : val.toString()) }]);
    setData(prev => ({ ...prev, [currentPhase.key]: val, ...extraData }));
    const nextPhase = typeof currentPhase.next === 'function' ? currentPhase.next(val) : currentPhase.next;
    setPhase(nextPhase);
  };

  const currentPhaseDef = phases[phase];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-end justify-center sm:items-center p-0 sm:p-4 bg-slate-950">
      <div className="w-full h-full sm:h-[90vh] sm:max-w-md bg-slate-900 flex flex-col sm:rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/5">
        <div className="flex items-center justify-between p-6 border-b border-white/5 bg-slate-900/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <SafeIcon icon="Zap" className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white tracking-tight">MaDaNRG Advisor</h3>
              <div className="flex items-center gap-1.5 text-[10px] text-emerald-400 font-bold uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span> Expert Online
              </div>
            </div>
          </div>
          <button onClick={onClose} title="Reset" className="p-2 text-slate-500 hover:text-white bg-white/5 rounded-xl transition-colors">
            <SafeIcon icon="RefreshCw" className="w-4 h-4" />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
          {messages.map((m) => <ChatMessage key={m.id} message={m} />)}
          {isTyping && <ChatMessage isTyping={true} message={{ sender: 'ai' }} />}
        </div>

        <div className="p-6 bg-slate-800/30 border-t border-white/5 min-h-[180px] flex items-center justify-center">
          <AnimatePresence mode="wait">
            {!isTyping && phase !== 'calculating' && currentPhaseDef && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full">
                {currentPhaseDef.type === 'search-select' && (
                  <div className="grid grid-cols-2 gap-2 max-h-[220px] overflow-y-auto pr-2">
                    {currentPhaseDef.options.map(opt => (
                      <button key={opt} onClick={() => handleInputSubmit(opt)} className="px-3 py-3 bg-slate-800 hover:bg-emerald-500 text-slate-300 hover:text-white text-xs font-semibold rounded-xl border border-white/5 transition-all text-left">{opt}</button>
                    ))}
                  </div>
                )}

                {currentPhaseDef.type === 'city-select' && (
                  <div className="grid grid-cols-1 gap-2 max-h-[220px] overflow-y-auto pr-2">
                    {SOLAR_DATASET.filter(s => s.country === data.country).map(city => (
                      <button key={city.city} onClick={() => handleInputSubmit(city.city, city.city, { peak_sun_hours: city.psh })} className="px-4 py-3.5 bg-slate-800 hover:bg-emerald-500 text-slate-200 hover:text-white text-sm rounded-xl border border-white/5 transition-all flex justify-between items-center group">
                        {city.city}
                        <span className="text-[10px] text-slate-500 group-hover:text-white/60 font-bold">PSH: {city.psh}</span>
                      </button>
                    ))}
                  </div>
                )}

                {currentPhaseDef.type === 'options' && (
                  <div className="flex flex-col gap-2">
                    {currentPhaseDef.options.map((opt) => (
                      <button key={opt} onClick={() => handleInputSubmit(opt)} className="w-full py-3.5 bg-slate-800 hover:bg-emerald-500 text-slate-200 hover:text-white text-sm font-bold rounded-xl border border-white/5 transition-all">{opt}</button>
                    ))}
                  </div>
                )}

                {currentPhaseDef.type === 'multi-categorized' && (
                  <div className="flex flex-col gap-5 max-h-[380px] overflow-y-auto pr-2">
                    {Object.entries(APPLIANCE_CATS).map(([cat, apps]) => (
                      <div key={cat} className="space-y-2.5">
                        <h4 className="text-[10px] uppercase font-black text-slate-500 tracking-[0.15em] ml-1">{cat}</h4>
                        <div className="flex flex-wrap gap-2">
                          {apps.map(app => (
                            <button key={app} onClick={() => {
                              const current = data.appliances || [];
                              setData({ ...data, appliances: current.includes(app) ? current.filter(a => a !== app) : [...current, app] });
                            }} className={`px-3.5 py-2 rounded-xl text-[11px] font-bold transition-all border ${data.appliances.includes(app) ? 'bg-emerald-500 border-emerald-400 text-white shadow-lg shadow-emerald-500/20' : 'bg-slate-800 border-white/5 text-slate-400'}`}>
                              {app}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    <button disabled={data.appliances.length === 0} onClick={() => handleInputSubmit(data.appliances)} className="w-full py-4 bg-emerald-500 text-white rounded-2xl font-black shadow-xl shadow-emerald-500/20 sticky bottom-0">Confirm Selection</button>
                  </div>
                )}

                {currentPhaseDef.type === 'counts' && (
                  <div className="flex flex-col gap-3 max-h-[250px] overflow-y-auto">
                    {data.appliances.map(app => (
                      <div key={app} className="flex items-center justify-between bg-slate-800/50 p-3.5 rounded-xl border border-white/5">
                        <span className="text-xs text-slate-300 font-bold">{app}</span>
                        <div className="flex gap-1">
                          {[1, 2, '3+'].map(num => (
                            <button key={num} onClick={() => setData({ ...data, appliance_count: { ...data.appliance_count, [app]: num } })} className={`w-10 py-2 rounded-lg text-[10px] font-black transition-all ${data.appliance_count[app] === num ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-slate-400'}`}>{num}</button>
                          ))}
                        </div>
                      </div>
                    ))}
                    <button onClick={() => handleInputSubmit(data.appliance_count, "Confirmed Counts")} className="w-full py-4 bg-emerald-500 text-white rounded-2xl font-black mt-2">Next: Usage Hours</button>
                  </div>
                )}

                {currentPhaseDef.type === 'hours-dropdown' && (
                  <div className="flex flex-col gap-3 max-h-[300px] overflow-y-auto pr-2">
                    {data.appliances.map(app => (
                      <div key={app} className="flex items-center justify-between bg-slate-800/50 p-3.5 rounded-xl border border-white/5">
                        <span className="text-xs text-slate-300 font-bold">{app}</span>
                        <select 
                          value={data.appliance_hours[app] || 0}
                          onChange={(e) => setData({ ...data, appliance_hours: { ...data.appliance_hours, [app]: parseInt(e.target.value) } })}
                          className="bg-slate-900 text-emerald-400 text-xs font-black py-2 px-3 rounded-lg border border-white/10 outline-none focus:border-emerald-500"
                        >
                          {HOUR_OPTIONS.map(h => <option key={h} value={h}>{h} hrs/day</option>)}
                        </select>
                      </div>
                    ))}
                    <button onClick={() => handleInputSubmit(data.appliance_hours, "Usage hours confirmed")} className="w-full py-4 bg-emerald-500 text-white rounded-2xl font-black mt-2 sticky bottom-0">Continue</button>
                  </div>
                )}

                {currentPhaseDef.type === 'contact-form' && (
                  <ContactForm onSubmit={(f) => {
                    handleInputSubmit(f.name, `Thanks, my name is ${f.name}`, { ...f });
                    setPhase('calculating');
                  }} />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}

function ContactForm({ onSubmit }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const isValid = form.name.trim() && form.email.includes('@');
  return (
    <div className="space-y-4">
      <div className="grid gap-3">
        <input placeholder="Full Name" className="bg-slate-900 border border-white/10 rounded-xl px-5 py-4 text-white text-sm focus:border-emerald-500 outline-none" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        <input placeholder="Email Address" type="email" className="bg-slate-900 border border-white/10 rounded-xl px-5 py-4 text-white text-sm focus:border-emerald-500 outline-none" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
        <input placeholder="Phone Number" type="tel" className="bg-slate-900 border border-white/10 rounded-xl px-5 py-4 text-white text-sm focus:border-emerald-500 outline-none" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
      </div>
      <button disabled={!isValid} onClick={() => onSubmit(form)} className="w-full py-4 bg-emerald-500 text-white rounded-2xl font-black text-lg shadow-xl shadow-emerald-500/20 disabled:opacity-50 transition-all">👉 Get My Energy Plan</button>
    </div>
  );
}